// src/pages/customer-panel/profile/ProfilePage.js
// User Profile Page - View and edit user profile details

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    Avatar,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Chip,
    Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

export default function ProfilePage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || 'customer';

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: '',
    });

    const fetchProfile = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/profile');
            const data = response.data?.data || response.data;
            setProfile(data);
            setFormData({
                first_name: data.first_name || data.fname || '',
                last_name: data.last_name || data.lname || '',
                email: data.email || '',
                phone: data.phone || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                zip_code: data.zip_code || data.zipCode || '',
                country: data.country || '',
            });
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            setError(err.response?.data?.message || 'Failed to load profile data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'My Profile | Customer Panel';
        fetchProfile();
    }, [fetchProfile]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put('/profile', formData);
            setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
            setEditMode(false);
            fetchProfile();
        } catch (err) {
            console.error('Failed to save profile:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to update profile.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formDataUpload = new FormData();
        formDataUpload.append('avatar', file);

        try {
            await axiosInstance.post('/profile/avatar', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setSnackbar({ open: true, message: 'Avatar uploaded successfully!', severity: 'success' });
            fetchProfile();
        } catch (err) {
            console.error('Failed to upload avatar:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to upload avatar.',
                severity: 'error',
            });
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        if (profile) {
            setFormData({
                first_name: profile.first_name || profile.fname || '',
                last_name: profile.last_name || profile.lname || '',
                email: profile.email || '',
                phone: profile.phone || '',
                address: profile.address || '',
                city: profile.city || '',
                state: profile.state || '',
                zip_code: profile.zip_code || profile.zipCode || '',
                country: profile.country || '',
            });
        }
    };

    const getInitials = () => {
        const first = formData.first_name?.charAt(0) || '';
        const last = formData.last_name?.charAt(0) || '';
        return (first + last).toUpperCase() || '?';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: GREEN_ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography
                        variant="h4"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}
                    >
                        My Profile
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        Manage your personal information and preferences
                    </Typography>
                </Box>
                <IconButton onClick={fetchProfile} sx={{ color: '#64748b' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Avatar Section */}
            <Paper
                sx={{
                    p: 4,
                    mb: 3,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    textAlign: 'center',
                }}
            >
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                        src={profile?.avatar_url || profile?.avatarUrl || ''}
                        sx={{
                            width: 120,
                            height: 120,
                            fontSize: '2.5rem',
                            fontFamily,
                            fontWeight: 700,
                            bgcolor: GREEN_ACCENT,
                            mx: 'auto',
                        }}
                    >
                        {getInitials()}
                    </Avatar>
                    <IconButton
                        component="label"
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            bgcolor: GREEN_ACCENT,
                            color: 'white',
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        <CameraAltIcon fontSize="small" />
                        <input type="file" hidden accept="image/*" onChange={handleAvatarUpload} />
                    </IconButton>
                </Box>
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mt: 2 }}>
                    {formData.first_name} {formData.last_name}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                    {formData.email}
                </Typography>
                <Chip
                    label={userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                    size="small"
                    sx={{
                        mt: 1,
                        fontFamily,
                        fontWeight: 600,
                        bgcolor: `${GREEN_ACCENT}15`,
                        color: GREEN_ACCENT,
                    }}
                />
            </Paper>

            {/* Profile Details */}
            <Paper
                sx={{
                    p: 4,
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        Personal Information
                    </Typography>
                    {!editMode ? (
                        <Button
                            startIcon={<EditIcon />}
                            variant="outlined"
                            onClick={() => setEditMode(true)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                borderColor: GREEN_ACCENT,
                                color: GREEN_ACCENT,
                                borderRadius: '10px',
                                textTransform: 'none',
                                '&:hover': { borderColor: '#059669', bgcolor: `${GREEN_ACCENT}08` },
                            }}
                        >
                            Edit Profile
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                startIcon={<CancelIcon />}
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    borderColor: '#e2e8f0',
                                    color: '#64748b',
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    bgcolor: GREEN_ACCENT,
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#059669' },
                                }}
                            >
                                Save Changes
                            </Button>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                First Name
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.first_name || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Last Name
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.last_name || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Email
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.email || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PhoneIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Phone
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.phone || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Address
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.address || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                City
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.city || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                State
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="state"
                                value={formData.state}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.state || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Zip Code
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="zip_code"
                                value={formData.zip_code}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.zip_code || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Country
                            </Typography>
                        </Box>
                        {editMode ? (
                            <TextField
                                fullWidth
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                size="small"
                                sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                            />
                        ) : (
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                {formData.country || '--'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <BadgeIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Role
                            </Typography>
                        </Box>
                        <Chip
                            label={userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                bgcolor: `${GREEN_ACCENT}15`,
                                color: GREEN_ACCENT,
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
